<template>
  <div>
    <v-card>
      <v-row>
        <v-col class="d-flex flex-column" cols="12" sm="6" lg="3">
          <v-select
            dense
            outlined
            :label="$t('common.gameweek')"
            :items="gameweeks"
            v-model="gameweekName"
          ></v-select>
        </v-col>
        <v-spacer></v-spacer>
        <div class="d-flex flex-column text-center pa-3">
          <v-chip label large>
            {{ competitions.competitionsFullName }}
          </v-chip>
        </div>
      </v-row>
      <v-divider class="mt-3"></v-divider>

      <div class="text-caption grey--text text--darken-1 my-2 px-1">
        Ikona <v-icon small color="primary">fas fa-pencil-alt</v-icon> włącza
        tryb edycji typu dla meczu, a
        <v-icon small color="primary">fas fa-save</v-icon>
        zapisuje wynik.
      </div>

      <!-- DESKTOP -->
      <v-data-table
        v-if="!isMobile"
        :headers="matchesTable.headers.desktop"
        :items="gameweekItems"
        :items-per-page="-1"
        :loading="matchesTable.loading"
        hide-default-footer
        group-by="match.matchDate"
        sort-by="match.matchDateTime"
        disable-sort
        :item-class="RowClass"
        class="elevation-1"
        no-data-text="Brak typów do wyświetlenia"
      >
        <template v-slot:[`group.header`]="{ items }">
          <th colspan="7">
            {{ items[0].match.matchDateFriendly }}
          </th>
        </template>
        <template v-slot:[`item.matchTime`]="{ item }">
          {{ item.match.matchTime }}
        </template>
        <template v-slot:[`item.homeTeamName`]="{ item }">
          {{ item.match.homeTeam.teamName }}
        </template>
        <template v-slot:[`item.awayTeamName`]="{ item }">
          {{ item.match.awayTeam.teamName }}
        </template>
        <template v-slot:[`item.teamsSpacer`]> - </template>
        <template v-slot:[`item.resultBet`]="{ item }">
          <div v-if="IsEditing(item)">
            <div
              class="d-flex"
              style="align-items: baseline; justify-content: center"
            >
              <div class="flex" style="width: 35px">
                <v-text-field
                  dense
                  outlined
                  hide-details
                  v-mask="'#'"
                  v-model.number="editState[item.match.matchId].home"
                ></v-text-field>
              </div>
              <div class="flex mx-1"><span>:</span></div>
              <div class="flex" style="width: 35px">
                <v-text-field
                  dense
                  outlined
                  hide-details
                  v-mask="'#'"
                  v-model.number="editState[item.match.matchId].away"
                ></v-text-field>
              </div>
              <v-btn
                icon
                small
                color="primary"
                class="ml-1 align-self-center"
                :loading="editState[item.match.matchId].loading"
                :title="$t('admin.saveBet')"
                @click="SaveBet(item)"
              >
                <v-icon small>fas fa-save</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                class="align-self-center"
                :title="$t('admin.cancel')"
                @click="CancelEdit(item)"
              >
                <v-icon small>fas fa-times</v-icon>
              </v-btn>
            </div>
          </div>
          <div v-else>
            <span v-if="!BetEmpty(item)">
              {{ item.matchResultBet.homeTeamScoreBet }} :
              {{ item.matchResultBet.awayTeamScoreBet }}
            </span>
            <v-icon small class="bet-result-icon ml-1">
              {{ BetResultIcon(item) }}
            </v-icon>
            <v-btn
              icon
              small
              color="primary"
              class="ml-1"
              :title="$t('admin.editBet')"
              @click="StartEdit(item)"
            >
              <v-icon small>fas fa-pencil-alt</v-icon>
            </v-btn>
          </div>
        </template>
        <template v-slot:[`item.resultActual`]="{ item }">
          <div v-if="MatchInProgress(item.match)">
            <v-progress-circular indeterminate size="20"></v-progress-circular>
          </div>
          <span v-else-if="MatchFinished(item.match)">
            {{ item.match.homeTeamScore }} :
            {{ item.match.awayTeamScore }}
          </span>
          <v-tooltip v-else-if="MatchPostponed(item.match)" bottom>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on"> przeł. </span>
            </template>
            <span> mecz został przełożony na inny termin </span>
          </v-tooltip>
          <span v-else> : </span>
        </template>
      </v-data-table>

      <!-- MOBILE -->
      <v-data-table
        v-if="isMobile"
        :headers="matchesTable.headers.mobile"
        :items="gameweekItems"
        :items-per-page="-1"
        :loading="matchesTable.loading"
        hide-default-footer
        group-by="match.matchDate"
        sort-by="match.matchDateTime"
        disable-sort
        :item-class="RowClass"
        class="elevation-1"
        no-data-text="Brak typów do wyświetlenia"
      >
        <template v-slot:[`group.header`]="{ items }">
          <th colspan="7">
            {{ items[0].match.matchDateFriendly }}
          </th>
        </template>
        <template v-slot:[`item.matchTime`]="{ item }">
          {{ item.match.matchTime }}
        </template>
        <template v-slot:[`item.teams`]="{ item }">
          {{ item.match.homeTeam.teamName }} -
          {{ item.match.awayTeam.teamName }}
        </template>
        <template v-slot:[`item.resultBet`]="{ item }">
          <div v-if="IsEditing(item)">
            <div
              class="d-flex"
              style="align-items: baseline; justify-content: end"
            >
              <v-btn
                icon
                small
                color="primary"
                class="mr-1 align-self-center"
                :loading="editState[item.match.matchId].loading"
                :title="$t('admin.saveBet')"
                @click="SaveBet(item)"
              >
                <v-icon small>fas fa-save</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                class="mr-1 align-self-center"
                :title="$t('admin.cancel')"
                @click="CancelEdit(item)"
              >
                <v-icon small>fas fa-times</v-icon>
              </v-btn>
              <div class="flex" style="width: 35px">
                <v-text-field
                  dense
                  outlined
                  hide-details
                  v-mask="'#'"
                  v-model.number="editState[item.match.matchId].home"
                ></v-text-field>
              </div>
              <div><span class="mx-1">:</span></div>
              <div class="flex" style="width: 35px">
                <v-text-field
                  dense
                  outlined
                  hide-details
                  v-mask="'#'"
                  v-model.number="editState[item.match.matchId].away"
                ></v-text-field>
              </div>
            </div>
          </div>
          <div v-else class="d-flex align-center justify-end">
            <v-btn
              icon
              small
              color="primary"
              class="mr-1"
              :title="$t('admin.editBet')"
              @click="StartEdit(item)"
            >
              <v-icon small>fas fa-pencil-alt</v-icon>
            </v-btn>
            <v-icon small class="bet-result-icon mr-1">
              {{ BetResultIcon(item) }}
            </v-icon>
            <span v-if="!BetEmpty(item)">
              {{ item.matchResultBet.homeTeamScoreBet }} :
              {{ item.matchResultBet.awayTeamScoreBet }}
            </span>
          </div>
        </template>
        <template v-slot:[`item.resultActual`]="{ item }">
          <div v-if="MatchInProgress(item.match)">
            <v-progress-circular indeterminate size="20"></v-progress-circular>
          </div>
          <span v-else-if="MatchFinished(item.match)">
            {{ item.match.homeTeamScore }} :
            {{ item.match.awayTeamScore }}
          </span>
          <v-tooltip v-else-if="MatchPostponed(item.match)" bottom>
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on"> przeł. </span>
            </template>
            <span> mecz został przełożony na inny termin </span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import AdminService from "@/services/AdminService.js";
import dayjs from "@/plugins/dayjs.js";

export default {
  name: "AdminBookmakerBets",
  props: {
    competitions: {
      type: Object,
      require: true,
    },
    betRecords: {
      type: Array,
      default: () => [],
    },
    userId: {
      type: String,
      require: true,
    },
  },
  data() {
    return {
      matchesTable: {
        headers: {
          desktop: [
            { value: "matchTime", width: "67px" },
            { value: "homeTeamName", align: "end" },
            { value: "teamsSpacer", align: "center", width: "1%" },
            { value: "awayTeamName", align: "start" },
            {
              value: "resultBet",
              text: "Typ",
              align: "center",
              width: "160px",
            },
            {
              value: "resultActual",
              text: "Wynik",
              align: "center",
              width: "65px",
            },
          ],
          mobile: [
            { value: "matchTime" },
            { value: "teams", align: "end" },
            { value: "resultBet", text: "Typ", align: "center" },
            {
              value: "resultActual",
              text: "Wynik",
              align: "center",
            },
          ],
        },
        loading: false,
      },
      gameweekName: "",
      editState: {},
    };
  },
  computed: {
    gameweekItems() {
      return this.betRecords.filter(
        (x) => x.match.gameweekName == this.gameweekName
      );
    },
    gameweeks() {
      return [
        ...new Set(this.betRecords.map((x) => x.match.gameweekName)),
      ].sort((a, b) => a - b);
    },
  },
  mounted() {
    this.$_setCurrentGameweek();
  },
  methods: {
    IsEditing(item) {
      const s = this.editState[item.match.matchId];
      return !!(s && s.editing);
    },
    StartEdit(item) {
      this.$set(this.editState, item.match.matchId, {
        editing: true,
        loading: false,
        home: item.matchResultBet.homeTeamScoreBet,
        away: item.matchResultBet.awayTeamScoreBet,
      });
    },
    CancelEdit(item) {
      this.$set(this.editState, item.match.matchId, {
        editing: false,
        loading: false,
        home: null,
        away: null,
      });
    },
    async SaveBet(item) {
      const matchId = item.match.matchId;
      const state = this.editState[matchId];
      const matchLabel = `${item.match.homeTeam.teamName} : ${item.match.awayTeam.teamName}`;

      const home = state.home === "" ? null : state.home;
      const away = state.away === "" ? null : state.away;

      if (home == null || away == null) {
        this.$vToastify.validationError("Aby zapisać typ, wpisz oba wyniki");
        return;
      }

      try {
        this.$set(this.editState[matchId], "loading", true);

        const response = await AdminService.UpdateUserBet(
          this.userId,
          matchId,
          {
            homeTeamScoreBet: home,
            awayTeamScoreBet: away,
          }
        );

        if (response.status === 200) {
          const data = response.data || {};
          const dbBet = data.data;

          // Safeguard: odzwierciedl autorytatywny stan z bazy dla tego meczu.
          item.matchResultBet.homeTeamScoreBet = dbBet
            ? dbBet.homeTeamScoreBet
            : null;
          item.matchResultBet.awayTeamScoreBet = dbBet
            ? dbBet.awayTeamScoreBet
            : null;

          if ((data.errors || [])[0]) {
            this.$vToastify.customWarning(data.errors[0]);
          } else {
            this.$vToastify.customSuccess(`Zapisano typ: ${matchLabel}`);
            this.CancelEdit(item);
            // Zapis meczu zakończonego przelicza punkty i ranking - przeładuj typy,
            // aby odświeżyć ikony wyników.
            this.$emit("bet-saved");
          }
        } else {
          this.$vToastify.customError(
            `Nie udało się zapisać typu: ${matchLabel}`
          );
        }
      } catch (err) {
        this.$vToastify.customError(
          `Nie udało się zapisać typu: ${matchLabel}`
        );
      } finally {
        if (this.editState[matchId]) {
          this.$set(this.editState[matchId], "loading", false);
        }
      }
    },
    BetEmpty(item) {
      return (
        (item.matchResultBet || {}).homeTeamScoreBet == null ||
        (item.matchResultBet || {}).awayTeamScoreBet == null
      );
    },
    MatchInProgress(match) {
      return (
        match.statusCode === "IN_PROGRESS" ||
        (match.statusCode === "NOT_STARTED" &&
          dayjs() > dayjs(match.matchDateTime))
      );
    },
    MatchFinished(match) {
      return match.statusCode === "FINISHED";
    },
    MatchPostponed(match) {
      return match.statusCode === "POSTPONED";
    },
    RowClass(item) {
      if (item.matchResultBet.betResult == 2) {
        return "betting-hit";
      }
      if (item.matchResultBet.betResult == 1) {
        return "betting-partially-hit";
      }
      if (item.matchResultBet.betResult == 0) {
        return "betting-missed";
      }
      if (!item.matchResultBet.betResult) {
        return "betting-empty";
      }
      return "";
    },
    BetResultIcon(item) {
      if (item.matchResultBet.betResult == 2) {
        return "fas fa-check-circle";
      }
      if (item.matchResultBet.betResult == 1) {
        return "fas fa-check-circle";
      }
      if (item.matchResultBet.betResult == 0) {
        return "fas fa-times-circle";
      }
      return "";
    },
    $_setCurrentGameweek() {
      const orderedRecords = (this.betRecords || [])
        .map((x) => x.match)
        .sort((a, b) => dayjs(a.matchDateTime) - dayjs(b.matchDateTime));

      // Admin koryguje mecze już rozegrane - domyślnie pokaż najnowszą kolejkę.
      this.gameweekName = (
        orderedRecords[orderedRecords.length - 1] || {}
      ).gameweekName;
    },
  },
};
</script>

<style>
.betting-hit i.bet-result-icon {
  color: #71fb00 !important;
}
.betting-partially-hit i.bet-result-icon {
  color: #ffcc00 !important;
}
.betting-missed i.bet-result-icon {
  color: #fb4040 !important;
}
</style>
